import React from 'react';
import {
	Menu,
	MenuItem,
	ListItemText,
	ListSubheader,
	Stack,
	Typography,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setThemeMode } from '@/features/userPrefs/userPrefsSlice';
import { openDialog } from '@/features/dialog/dialogSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import BrokerAvatar from '@/components/BrokerAvatar';
import { IS_DEV, IS_PRE } from '@/utils/environment';
import { DarkModeSwitch } from '@/components/buttons/DarkModeSwitch';
import { MobileMenu } from './MobileMenu';
import { paths } from '@/app/paths';
import { useAdminModeAccess } from '@/hooks/useAdminModeAccess';

export const UserMenu = ({ anchorEl, onClose, onSignOut }) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const isDarkMode = useSelector((state) => state.userPrefs.themeMode === 'dark');
	const handleToggleDarkMode = () => dispatch(setThemeMode(isDarkMode ? 'light' : 'dark'));

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const { getFeatureFlags } = useFeatureFlagContext();
	const showProfile = Boolean(getFeatureFlags('profile')?.active);
	const { canAccessAdminMode, hasBrokerGroups, isAdminMode } = useAdminModeAccess();
	const navigate = useNavigate();
	const showAdministration = canAccessAdminMode && hasBrokerGroups && !isAdminMode;
	const showCrm = isAdminMode && hasBrokerGroups;

	return (
		<Menu
			id='menu-appbar'
			anchorEl={anchorEl}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			keepMounted
			transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			open={Boolean(anchorEl)}
			onClose={onClose}
			{...((IS_DEV || IS_PRE) && {
				sx: {
					'& .MuiMenu-list': {
						pb: 0,
					},
				},
			})}
		>
			<ListSubheader sx={{ lineHeight: '36px', backgroundColor: 'transparent' }}>
				<Stack direction='row' width='100%' spacing={1} alignItems={'center'}>
					<BrokerAvatar broker={userDetails} />
					<Typography sx={{ fontSize: '12px' }}>{email}</Typography>
				</Stack>
			</ListSubheader>
			{showAdministration && (
				<MenuItem
					onClick={() => {
						navigate(paths.adminUsers);
						onClose();
					}}
				>
					Administration
				</MenuItem>
			)}
			{showCrm && (
				<MenuItem
					onClick={() => {
						navigate(paths.home);
						onClose();
					}}
				>
					CRM
				</MenuItem>
			)}
			{isMobile && <MobileMenu />}
			{showProfile && (
				<MenuItem
					onClick={() => {
						dispatch(
							openDialog({
								dialogName: 'userProfile',
								data: { userId: userDetails?.userId },
							}),
						);
						onClose();
					}}
				>
					Profile
				</MenuItem>
			)}
			<MenuItem onClick={handleToggleDarkMode} sx={{ justifyContent: 'space-between' }}>
				<ListItemText>{isDarkMode ? 'Dark mode on' : 'Light mode on'}</ListItemText>
				<DarkModeSwitch />
			</MenuItem>
			<MenuItem onClick={onSignOut}>Sign out</MenuItem>
			{(IS_DEV || IS_PRE) && (
				<ListSubheader
					sx={{
						backgroundColor: IS_DEV
							? theme.palette.primary.main
							: theme.palette.secondary.main,
						py: 0.5,
					}}
				>
					<Typography
						sx={{
							fontSize: '10px',
							color: theme.palette.getContrastText(
								IS_DEV ? theme.palette.primary.main : theme.palette.secondary.main,
							),
						}}
					>
						{IS_DEV ? 'DEV' : 'PRE'}
					</Typography>
				</ListSubheader>
			)}
		</Menu>
	);
};
